import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
    CreditCard,
    ArrowLeft,
    CheckCircle,
    XCircle,
    Clock,
    DollarSign,
    Building,
    Loader2,
    AlertCircle,
    RefreshCw,
    Users,
    ExternalLink,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { dashboardService } from '@/services/dashboardService'
import { Transaction } from '@/types'
import { safeFormatDistanceToNow, isValidDate } from '@/utils/date'

const statusConfig = {
    pending: {
        label: 'Pending',
        color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
        icon: Clock,
    },
    processing: {
        label: 'Processing',
        color: 'text-blue-600 bg-blue-50 border-blue-200',
        icon: RefreshCw,
    },
    completed: {
        label: 'Completed',
        color: 'text-green-600 bg-green-50 border-green-200',
        icon: CheckCircle,
    },
    failed: {
        label: 'Failed',
        color: 'text-orange-600 bg-orange-50 border-orange-200',
        icon: XCircle,
    },
    cancelled: {
        label: 'Cancelled',
        color: 'text-gray-600 bg-gray-50 border-gray-200',
        icon: XCircle,
    },
    refunded: {
        label: 'Refunded',
        color: 'text-purple-600 bg-purple-50 border-purple-200',
        icon: AlertCircle,
    },
}

const paymentMethodConfig = {
    card: { label: 'Card', icon: CreditCard },
    bank_transfer: { label: 'Bank Transfer', icon: Building },
    mobile_money: { label: 'Mobile Money', icon: Users },
    crypto: { label: 'Crypto', icon: DollarSign },
    other: { label: 'Other', icon: CreditCard },
}

export default function TransactionDetail() {
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const cardStyles = getCardStyles(branding)
    const statusColors = getStatusColors(branding)
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [transaction, setTransaction] = useState<Transaction | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (id) {
            fetchTransactionData()
        }
    }, [id])

    const fetchTransactionData = async () => {
        if (!id) return

        try {
            setLoading(true)
            setError(null)

            const response = await dashboardService.getTransaction(parseInt(id))

            // Handle wrapped response structure {success, message, data}
            if ((response as any).success && (response as any).data) {
                setTransaction((response as any).data)
            } else if ((response as any).id) {
                // Direct transaction object (no wrapper)
                setTransaction(response)
            } else {
                setError('Invalid transaction data format')
            }
        } catch (err: any) {
            const errorMessage =
                err.response?.data?.message ||
                err.message ||
                'An error occurred while fetching transaction data'
            setError(errorMessage)
            console.error('Error fetching transaction data:', err)
        } finally {
            setLoading(false)
        }
    }

    const getStatusConfig = (status: string) => {
        return statusConfig[status as keyof typeof statusConfig] || statusConfig.pending
    }

    const getPaymentMethodConfig = (method?: string) => {
        if (!method) return paymentMethodConfig.other
        return (
            paymentMethodConfig[method as keyof typeof paymentMethodConfig] ||
            paymentMethodConfig.other
        )
    }

    const formatAmount = (amount: string | number | null | undefined, currencyCode?: string) => {
        if (!amount) return 'N/A'

        const amountStr = typeof amount === 'string' ? amount : String(amount)
        const numAmount = parseFloat(amountStr)

        if (isNaN(numAmount)) {
            console.warn('Invalid amount:', amount)
            return 'N/A'
        }

        const currencySymbol = currencyCode === 'USD' ? '$' : currencyCode || '$'
        return `${currencySymbol}${numAmount.toFixed(2)}`
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: branding.primaryColor }} />
                    <p style={pageStyles.textSecondary}>Loading transaction...</p>
                </div>
            </div>
        )
    }

    if (error || !transaction) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <AlertCircle className="h-8 w-8 mx-auto mb-4" style={{ color: statusColors.error.color }} />
                    <p className="mb-4" style={{ color: statusColors.error.color }}>
                        {error || 'Transaction not found'}
                    </p>
                    <Button
                        onClick={() => navigate('/transactions')}
                        variant="outline"
                        style={{
                            color: branding.textSecondaryColor,
                            borderColor: `${branding.textSecondaryColor}40`,
                            backgroundColor: 'transparent',
                        }}
                    >
                        Back to Transactions
                    </Button>
                </div>
            </div>
        )
    }

    const statusConfigItem = getStatusConfig(transaction.status)
    const StatusIcon = statusConfigItem.icon
    const paymentMethodConfigItem = getPaymentMethodConfig(transaction.payment_method)
    const PaymentMethodIcon = paymentMethodConfigItem.icon

    // Get customer info from API response (may have customer_name/customer_email even if full_name/email are null)
    const customerName = (transaction as any).customer_name || transaction.full_name
    const customerEmail = (transaction as any).customer_email || transaction.email
    const customerPhone = transaction.phone || (transaction.metadata?.customer_phone as string) || null
    const saleShopName = (transaction as any).sale_shop_name
    const currencyCode = (transaction as any).currency_code || 'USD'
    const currencyName = (transaction as any).currency_name

    return (
        <div className="min-h-screen" style={pageStyles.container}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/transactions')}
                                style={{ color: branding.textSecondaryColor }}
                                className="hover:text-white"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back to Transactions
                            </Button>
                            <div>
                                <h1 className="text-3xl font-bold" style={pageStyles.text}>
                                    Transaction #{transaction.transaction_reference}
                                </h1>
                                {transaction.external_reference && (
                                    <p className="mt-1 text-sm" style={pageStyles.textSecondary}>
                                        External: {transaction.external_reference}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusConfigItem.color}`}
                            >
                                <StatusIcon className="h-4 w-4 mr-2" />
                                {statusConfigItem.label}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Transaction Information */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Basic Information */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h2 className="text-lg font-semibold mb-4" style={pageStyles.text}>
                                Transaction Information
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-sm font-medium" style={pageStyles.textSecondary}>
                                        Transaction Reference
                                    </label>
                                    <p className="mt-1 text-sm font-mono px-3 py-1 rounded" style={{
                                        color: branding.textSecondaryColor,
                                        backgroundColor: `${branding.textSecondaryColor}20`
                                    }}>
                                        {transaction.transaction_reference}
                                    </p>
                                </div>
                                {transaction.external_reference && (
                                    <div>
                                        <label className="text-sm font-medium" style={pageStyles.textSecondary}>
                                            External Reference
                                        </label>
                                        <p className="mt-1 text-sm font-mono px-3 py-1 rounded" style={{
                                            color: branding.textSecondaryColor,
                                            backgroundColor: `${branding.textSecondaryColor}20`
                                        }}>
                                            {transaction.external_reference}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <label className="text-sm font-medium" style={pageStyles.textSecondary}>
                                        Created
                                    </label>
                                    <p className="mt-1 text-sm" style={pageStyles.textSecondary}>
                                        {transaction.created_at && isValidDate(transaction.created_at)
                                            ? new Date(transaction.created_at).toLocaleString()
                                            : 'N/A'}
                                    </p>
                                    <p className="mt-1 text-xs" style={pageStyles.textSecondary}>
                                        {safeFormatDistanceToNow(transaction.created_at, 'Unknown', { addSuffix: true })}
                                    </p>
                                </div>
                                {transaction.completed_at && (
                                    <div>
                                        <label className="text-sm font-medium" style={pageStyles.textSecondary}>
                                            Completed
                                        </label>
                                        <p className="mt-1 text-sm" style={pageStyles.textSecondary}>
                                            {transaction.completed_at && isValidDate(transaction.completed_at)
                                                ? new Date(transaction.completed_at).toLocaleString()
                                                : 'N/A'}
                                        </p>
                                        <p className="mt-1 text-xs" style={pageStyles.textSecondary}>
                                            {safeFormatDistanceToNow(transaction.completed_at, 'Unknown', { addSuffix: true })}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <label className="text-sm font-medium" style={pageStyles.textSecondary}>
                                        Last Updated
                                    </label>
                                    <p className="mt-1 text-sm" style={pageStyles.textSecondary}>
                                        {transaction.updated_at && isValidDate(transaction.updated_at)
                                            ? new Date(transaction.updated_at).toLocaleString()
                                            : 'N/A'}
                                    </p>
                                    <p className="mt-1 text-xs" style={pageStyles.textSecondary}>
                                        {safeFormatDistanceToNow(transaction.updated_at, 'Unknown', { addSuffix: true })}
                                    </p>
                                </div>
                                {currencyCode && (
                                    <div>
                                        <label className="text-sm font-medium" style={pageStyles.textSecondary}>
                                            Currency
                                        </label>
                                        <p className="mt-1 text-sm" style={pageStyles.textSecondary}>
                                            {currencyCode} {currencyName ? `(${currencyName})` : ''}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Payment Details */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h2 className="text-lg font-semibold mb-4" style={pageStyles.text}>
                                Payment Details
                            </h2>
                            <div className="space-y-6">
                                {/* Amount */}
                                <div
                                    className="text-center p-6 rounded-lg"
                                    style={{ backgroundColor: `${statusColors.success.backgroundColor}20` }}
                                >
                                    <div className="flex items-center justify-center mb-2">
                                        <DollarSign className="h-8 w-8" style={{ color: statusColors.success.color }} />
                                    </div>
                                    <p className="text-sm font-medium" style={pageStyles.textSecondary}>
                                        Transaction Amount
                                    </p>
                                    <p className="text-4xl font-bold" style={pageStyles.text}>
                                        {formatAmount(transaction.total_amount, currencyCode)}
                                    </p>
                                    {currencyName && (
                                        <p className="text-xs mt-1" style={pageStyles.textSecondary}>
                                            {currencyName}
                                        </p>
                                    )}
                                </div>

                                {/* Payment Method */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="text-sm font-medium" style={pageStyles.textSecondary}>
                                            Payment Method
                                        </label>
                                        <div className="mt-1 flex items-center">
                                            <PaymentMethodIcon
                                                className="h-4 w-4 mr-2"
                                                style={{ color: branding.textSecondaryColor }}
                                            />
                                            <p className="text-sm" style={pageStyles.textSecondary}>
                                                {paymentMethodConfigItem.label} - to - Crypto
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Customer Information */}
                        {(customerName || customerEmail || customerPhone || transaction.customer) && (
                            <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                                <h2 className="text-lg font-semibold mb-4" style={pageStyles.text}>
                                    Customer Information
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {customerName && (
                                        <div>
                                            <label className="text-sm font-medium" style={pageStyles.textSecondary}>
                                                Full Name
                                            </label>
                                            <p className="mt-1 text-sm" style={pageStyles.textSecondary}>
                                                {customerName}
                                            </p>
                                        </div>
                                    )}
                                    {customerEmail && (
                                        <div>
                                            <label className="text-sm font-medium" style={pageStyles.textSecondary}>
                                                Email
                                            </label>
                                            <p className="mt-1 text-sm" style={pageStyles.textSecondary}>
                                                {customerEmail}
                                            </p>
                                        </div>
                                    )}
                                    {customerPhone && (
                                        <div>
                                            <label className="text-sm font-medium" style={pageStyles.textSecondary}>
                                                Phone
                                            </label>
                                            <p className="mt-1 text-sm" style={pageStyles.textSecondary}>
                                                {customerPhone}
                                            </p>
                                        </div>
                                    )}
                                    {transaction.customer && (
                                        <div>
                                            <label className="text-sm font-medium" style={pageStyles.textSecondary}>
                                                Customer ID
                                            </label>
                                            <p className="mt-1 text-sm" style={pageStyles.textSecondary}>
                                                #{transaction.customer}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Status Sidebar */}
                    <div className="space-y-6">
                        {/* Status Information */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h3 className="text-lg font-semibold mb-4" style={pageStyles.text}>
                                Status Information
                            </h3>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm" style={pageStyles.textSecondary}>
                                        Current Status
                                    </span>
                                    <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusConfigItem.color}`}
                                    >
                                        <StatusIcon className="h-3 w-3 mr-1" />
                                        {statusConfigItem.label}
                                    </span>
                                </div>
                                {transaction.completed_at && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm" style={pageStyles.textSecondary}>
                                            Completed
                                        </span>
                                        <span className="text-sm" style={pageStyles.textSecondary}>
                                            {safeFormatDistanceToNow(transaction.completed_at, 'Unknown', { addSuffix: true })}
                                        </span>
                                    </div>
                                )}

                            </div>
                        </div>

                        {/* Related Information */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h3 className="text-lg font-semibold mb-4" style={pageStyles.text}>
                                Related Information
                            </h3>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm" style={pageStyles.textSecondary}>
                                        Sale Shop
                                    </span>
                                    <div className="flex flex-col items-end">
                                        {saleShopName && (
                                            <span className="text-sm font-medium mb-1" style={pageStyles.text}>
                                                {saleShopName}
                                            </span>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => navigate(`/sale-shops/${transaction.sale_shop}`)}
                                            style={{ color: branding.primaryColor }}
                                            className="hover:opacity-80"
                                        >
                                            View Shop
                                            <ExternalLink className="h-3 w-3 ml-1" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

